import axios from "axios";

export interface Shift {
  id?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  workStartDate?: string;
  workEndDate?: string;
  breakDuration?: number;
  plannedPayment?: number;
  actualPayment?: number;
  dispute?: boolean;
  userPhoto?: string;
  userRole?: string;
}

export enum PinErrorCode {
  InvalidPin = "invalid",
  FailedToVerifyPin = "failed",
}

export interface PlannedShift extends Shift {
  plannedStartDate: string;
  plannedEndDate: string;
}

export interface PastShift extends Shift {
  workStartDate: string;
  workEndDate: string;
}

export function getShiftsByDate(params: {
  StartDate: string;
  EndDate: string;
  LocationId: string;
}) {
  return axios
    .get<Shift[]>(`/Shifts/getshiftsbylocationdate`, { params })
    .then((response) => response.data);
}

export interface AddShift {
  plannedStartDate?: string;
  plannedEndDate?: string;
  workStartDate?: string;
  workEndDate?: string;
  isPeriod?: boolean;
  actualPayment?: number;
  plannedPayment?: number;
  companyId: string;
  companyLocationId: string;
  userId: string;
  breakDuration?: number;
}

export interface AddDailyOpenShift {
  plannedStartDate?: string;
  plannedEndDate?: string;
  isPeriod?: boolean;
  companyId: string;
  companyLocationId: string;
}

export interface StaffShiftAnalysis {
  id: string;
  firstName: string;
  lastName: string;
  userRole: number;
  userRoleName: string;
  totalWorkingHours: number;
  wageType: number;
  wageTypeName: string;
  fee: number;
  wageAmount: number;
}

export function addShift({
  plannedStartDate,
  plannedEndDate,
  ...shift
}: AddShift) {
  return axios
    .post(`/Shifts/addshift`, {
      plannedStartTime: plannedStartDate,
      plannedEndTime: plannedEndDate,
      ...shift,
    })
    .then((response) => response.data);
}

export interface AddBulkShift {
  companyId: string;
  companyLocationId: string;
  users: string[];
  shiftDates: {
    plannedStartTime: string;
    plannedEndTime: string;
  }[];
}

export interface UpdateBulkShift {
  updateShiftDtos: UpdateShiftsDto[];
}

export interface UpdateShiftsDto {
  id: string;
  plannedStartTime: string;
  plannedEndTime: string;
  workStartDate: string;
  workEndDate: string;
  isPeriod?: boolean;
  actualPayment?: number;
  plannedPayment?: number;
  companyId: string;
  companyLocationId: string;
  userId: string;
  breakDuration: number;
}

export interface CopyShiftsDayPayload {
  copyStartDate: string;
  copyEndDate: string;
  destinationShiftDates: {
    assignStartDate: string;
    assignEndDate: string;
  }[];
  companyLocationId: string;
  companyId: string;
  users: string[];
}

export function addBulkShifts(body: AddBulkShift) {
  return axios
    .post(`/Shifts/addbulkshift`, body)
    .then((response) => response.data);
}

export interface UpdateShift extends AddShift {
  id: string;
}

export function updateShift({
  plannedStartDate,
  plannedEndDate,
  ...shift
}: UpdateShift) {
  return axios
    .put(`/Shifts/updateshift`, {
      plannedStartTime: plannedStartDate,
      plannedEndTime: plannedEndDate,
      ...shift,
    })
    .then((response) => response.data);
}

export function deleteShift(id: string) {
  return axios
    .delete(`/Shifts/deleteshift`, { data: { id } })
    .then((response) => response.data);
}

export async function deleteMultipleShifts(ids: string[]) {
  await Promise.all(ids.map((id) => deleteShift(id)));
  return { data: "success" };
}

export async function deleteBulkShift(shiftIds: string[]) {
  return axios
    .delete(`/Shifts/deletebulkshift`, { data: { shiftIds } })
    .then((response) => response.data);
}

export async function addOpenShift(payloads: AddDailyOpenShift[]) {
  await Promise.all(
    payloads.map((payload) =>
      axios
        .post("/Shifts/addopenshift", payload)
        .then((response) => response.data),
    ),
  );
  return { data: "success" };
}

export function checkPin(params: {
  LocationId: string;
  UserId: string;
  UserLocationPin: string;
}) {
  return axios
    .get("/CompanyLocationUsers/checklocationuserpin", { params })
    .then((response) => response.data)
    .catch(() => {
      throw new Error(PinErrorCode.FailedToVerifyPin);
    });
}

export function addShiftWithPin({ pin, ...shift }: AddShift & { pin: string }) {
  return checkPin({
    LocationId: shift.companyLocationId,
    UserId: shift.userId,
    UserLocationPin: pin,
  }).then((response) => {
    if (response) {
      return addShift(shift);
    }
    throw Error(PinErrorCode.InvalidPin);
  });
}

export function updateShiftWithPin({
  pin,
  ...shift
}: UpdateShift & { pin: string }) {
  return checkPin({
    LocationId: shift.companyLocationId,
    UserId: shift.userId,
    UserLocationPin: pin,
  }).then((response) => {
    if (response) {
      return updateShift(shift);
    }
    throw Error(PinErrorCode.InvalidPin);
  });
}

export function updateShiftBreakWithPin(params: {
  id: string;
  pin: string;
  companyId: string;
  companyLocationId: string;
  userId: string;
  breakDuration: number;
}) {
  return checkPin({
    LocationId: params.companyLocationId,
    UserId: params.userId,
    UserLocationPin: params.pin,
  }).then((response) => {
    if (response) {
      return axios
        .put(`/Shifts/updateshiftbreak`, {
          id: params.id,
          breakDuration: params.breakDuration,
        })
        .then((response) => response.data);
    }
    throw Error(PinErrorCode.InvalidPin);
  });
}

export function copyShifts(payload: CopyShiftsDayPayload) {
  return axios
    .post(`/Shifts/copyusershifttoweeks`, payload)
    .then((response) => response.data);
}

export function getStaffingExpense(params: {
  companyId: string;
  locationId: string;
  startDate: string;
  endDate: string;
}) {
  return axios
    .get<
      StaffShiftAnalysis[]
    >(`/Shifts/staffingexpenseanalyzerbydate`, { params })
    .then((response) => response.data);
}

export interface UpdateOpenShiftPayload {
  id: string;
  plannedStartTime: string;
  plannedEndTime: string;
}

export function updateOpenShift(payload: UpdateOpenShiftPayload) {
  return axios
    .put(`/Shifts/updateopenshift`, payload)
    .then((response) => response.data);
}

export function updateBulkShift(payload: UpdateBulkShift) {
  return axios
    .put(`/Shifts/updatebulkshift`, payload)
    .then((response) => response.data);
}

export function getShiftsByUser(params: {
  LocationId: string;
  StartDate: string;
  EndDate: string;
  UserId: string;
}) {
  return axios
    .get<Shift[]>(`/Shifts/getshiftsbyuserwithdate`, { params })
    .then((response) => response.data);
}
